
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

// Typography variants using cva for consistent styling
export const typographyVariants = cva("", {
  variants: {
    variant: {
      default: "text-mono-seasalt",
      muted: "text-mono-platinum/80",
      subtle: "text-mono-platinum/60",
      accent: "text-mono-frenchGray2",
      destructive: "text-destructive",
    },
    size: {
      display: "text-5xl md:text-6xl tracking-tight",
      h1: "text-3xl md:text-4xl tracking-tight",
      h2: "text-2xl md:text-3xl",
      h3: "text-xl md:text-2xl",
      h4: "text-lg md:text-xl",
      body: "text-base",
      small: "text-sm",
      xs: "text-xs",
    },
    weight: {
      regular: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "body",
    weight: "regular",
  },
})

export interface TypographyProps 
  extends React.HTMLAttributes<HTMLElement>, 
    VariantProps<typeof typographyVariants> {
  as?: React.ElementType
}

// Base Typography component
export const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, size, weight, as: Component = "div", ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn(typographyVariants({ variant, size, weight }), className)}
        {...props}
      />
    )
  }
)
Typography.displayName = "Typography"
